// Progressive navigation for the already filtered native product gallery.
const desktopQuery = '(min-width: 750px)';
const reducedQuery = '(prefers-reduced-motion: reduce)';
const headerHeight = () => {
  const header = document.querySelector('rs-header, #header-component, .rs-header');
  if (!header) return 76;
  const rect = header.getBoundingClientRect();
  return Math.max(0, Math.min(180, rect.bottom > 0 ? rect.bottom : rect.height));
};

class RSProductEditorial extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.desktop = window.matchMedia(desktopQuery);
    window.addEventListener('resize', this.queueMeasure, { signal: this.abort.signal, passive: true });
    this.mutations = new MutationObserver(this.observeCard);
    this.mutations.observe(this, { childList: true, subtree: true });
    if ('ResizeObserver' in window) this.resize = new ResizeObserver(this.queueMeasure);
    this.observeCard();
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.mutations?.disconnect();
    this.resize?.disconnect();
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    this.card = null;
  }
  observeCard = () => {
    const card = this.querySelector('.product-details');
    if (card !== this.card) {
      this.resize?.disconnect();
      this.card = card;
      if (card) this.resize?.observe(card);
      const header = document.querySelector('rs-header, #header-component, .rs-header');
      if (header) this.resize?.observe(header);
    }
    this.queueMeasure();
  };
  queueMeasure = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      if (!this.isConnected || !this.desktop.matches) return;
      const header = headerHeight();
      const height = this.card?.getBoundingClientRect().height || 0;
      // Tall cards scroll until their bottom fits, so lower content remains reachable.
      const stickyTop = Math.min(header + 16, window.innerHeight - height - 20);
      this.style.setProperty('--rs-editorial-header', `${Math.round(header)}px`);
      this.style.setProperty('--rs-editorial-card-top', `${Math.round(stickyTop)}px`);
    });
  };
}

class RSProductRail extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.desktop = window.matchMedia(desktopQuery);
    this.reduced = window.matchMedia(reducedQuery);
    const { signal } = this.abort;
    this.addEventListener('click', this.handleClick, { signal });
    this.addEventListener('keydown', this.handleKey, { signal });
    window.addEventListener('scroll', this.queueUpdate, { signal, passive: true });
    window.addEventListener('resize', this.queueUpdate, { signal, passive: true });
    queueMicrotask(() => {
      if (!this.isConnected) return;
      this.links = [...this.querySelectorAll('[data-rs-rail-index]')];
      const gallery = this.closest('media-gallery');
      const media = [...(gallery?.querySelectorAll('.media-gallery__grid > li') || [])];
      this.items = this.links.map((link) => media.find((item) => `#${item.id}` === link.getAttribute('href')));
      this.previous = this.querySelector('[data-rs-rail-step="-1"]');
      this.next = this.querySelector('[data-rs-rail-step="1"]');
      this.list = this.querySelector('.rs-product-rail__list');
      this.active = 0;
      if ('IntersectionObserver' in window) {
        this.intersection = new IntersectionObserver(this.queueUpdate, { rootMargin: '-20% 0px -35% 0px', threshold: [0, .25, .5, 1] });
        this.items.filter(Boolean).forEach((item) => this.intersection.observe(item));
      }
      this.dataset.ready = 'true';
      this.queueUpdate();
    });
  }
  disconnectedCallback() {
    this.abort?.abort();
    this.intersection?.disconnect();
    cancelAnimationFrame(this.frame);
    this.frame = 0;
  }
  handleClick = (event) => {
    const link = event.target.closest('[data-rs-rail-index]');
    const step = event.target.closest('[data-rs-rail-step]');
    if (!this.desktop.matches || (!link && !step)) return;
    const index = link ? Number(link.dataset.rsRailIndex) : this.active + Number(step.dataset.rsRailStep);
    if (!this.items?.[index]) return;
    event.preventDefault();
    this.goTo(index);
  };
  handleKey = (event) => {
    const link = event.target.closest('[data-rs-rail-index]');
    if (!link || !this.desktop.matches) return;
    const current = Number(link.dataset.rsRailIndex);
    let index;
    if (event.key === 'ArrowDown') index = Math.min(current + 1, this.links.length - 1);
    else if (event.key === 'ArrowUp') index = Math.max(current - 1, 0);
    else if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = this.links.length - 1;
    else return;
    event.preventDefault();
    this.links[index]?.focus({ preventScroll: true });
    this.goTo(index);
  };
  goTo(index) {
    const item = this.items?.[index];
    if (!item || !item.isConnected) return;
    const top = Math.max(0, window.scrollY + item.getBoundingClientRect().top - headerHeight() - 16);
    window.scrollTo({ top, behavior: this.reduced.matches ? 'auto' : 'smooth' });
    this.setActive(index);
  }
  queueUpdate = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      if (!this.isConnected || !this.desktop.matches || !this.items?.length) return;
      const focusLine = headerHeight() + (window.innerHeight - headerHeight()) * .4;
      let best = 0;
      let distance = Infinity;
      this.items.forEach((item, index) => {
        if (!item) return;
        const rect = item.getBoundingClientRect();
        const current = rect.top <= focusLine && rect.bottom >= focusLine ? 0 : Math.min(Math.abs(rect.top - focusLine), Math.abs(rect.bottom - focusLine));
        if (current < distance) { distance = current; best = index; }
      });
      this.setActive(best);
    });
  };
  setActive(index) {
    this.active = index;
    this.links?.forEach((link, i) => {
      if (i === index) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });
    if (this.previous) this.previous.disabled = index === 0;
    if (this.next) this.next.disabled = index === (this.items?.length || 0) - 1;
    const link = this.links?.[index];
    if (link && this.list) {
      const top = link.offsetTop - this.list.offsetTop;
      if (top < this.list.scrollTop) this.list.scrollTop = top;
      else if (top + link.offsetHeight > this.list.scrollTop + this.list.clientHeight) this.list.scrollTop = top + link.offsetHeight - this.list.clientHeight;
    }
  }
}

if (!customElements.get('rs-product-editorial')) customElements.define('rs-product-editorial', RSProductEditorial);
if (!customElements.get('rs-product-rail')) customElements.define('rs-product-rail', RSProductRail);
